import api from "@/api";
import { useAuthStore } from "@/stores/auth";
import { useMessengerStore } from "@/stores/messenger";
import { useUserStore } from "@/stores/user";
import { useToast } from "vue-toastification";

export const DEFAULT_RECONNECT_INTERVAL = 300;
export const DEFAULT_MAX_RECONNECT_ATTEMPT = 5000;

const DEDUPE_BUFFER_SIZE = 20;

/** Provide the shared transport and message handling used by WFB chat stores. */
export function useWfbChat() {
  const closeWebsocket = (store) => {
    if (store.websocket) {
      store.websocket.close();
      store.websocket.removeAllListeners();
    }
    store.websocket = null;
  };

  const sendMessage = async (store, message, requestId, attachments) => {
    const { handleAttachmentsUpload } = useMessengerStore();
    const tempId = `temp-${window.crypto.randomUUID()}`;

    store.history.set(tempId, {
      id: tempId,
      msg_type: "user",
      response: message,
      success: true,
      attachments: attachments.map((file) => ({
        filename: file.name,
        url: URL.createObjectURL(file),
      })),
    });

    const { transformedAttachments } =
      await handleAttachmentsUpload(attachments);

    store.websocket.send(
      JSON.stringify({
        version: "v1",
        session_id: store.sessionId,
        message,
        request_id: requestId,
        attachments: transformedAttachments,
        extras: store.messageExtras || {
          user_token: store.authToken,
        },
      }),
    );
  };

  const stopResponse = async (store, requestId) => {
    requestId = requestId || store.streamingRequestId;
    if (!requestId) {
      console.warn("stopResponse -> no request id for the in-flight query");
      return;
    }
    const stopResponseURL = `${import.meta.env.VITE_APP_AC_SERVER_HTTPS_URL}/api/agentchat/stop-response/${requestId}?token=${store.accessToken}`;
    const stopResponseBody = { session_id: store.sessionId };
    console.log("stopResponse -> request_id:", requestId, "body:", stopResponseBody);
    const stopResponseRes = await fetch(stopResponseURL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(stopResponseBody),
    });

    if (stopResponseRes.ok) {
      store.isStoppingResponse = true;
    }
    return stopResponseRes;
  };

  const fetchAuthTokens = async (store, includeLegacyTokens = false) => {
    const userStore = useUserStore();
    const authStore = useAuthStore();
    const { data } = await api.messenger.getAuthTokensForAcServer(
      userStore.activeOrganization,
    );

    store.accessToken = data.access_token;
    if (includeLegacyTokens) {
      store.refreshToken = data.refresh_token;
      store.orgToken = data.org_token;
    }
    store.authToken = (await authStore.getTokens()).accessToken;
  };

  const failWithMsg = (store, message) => {
    store.connectionStatus = "FAILED";
    useToast().error(message);
  };

  const isDuplicateMessagePayload = (store, payload) => {
    if (store.recentMessagePayloads.includes(payload)) return true;

    store.recentMessagePayloads.push(payload);
    while (store.recentMessagePayloads.length > DEDUPE_BUFFER_SIZE) {
      store.recentMessagePayloads.shift();
    }
    return false;
  };

  const mergeMessages = (
    store,
    data,
    { onDuplicate, onSystemError, onSystemMessage } = {},
  ) => {
    if (isDuplicateMessagePayload(store, data)) {
      onDuplicate?.(data);
      return;
    }

    const history = JSON.parse(data);

    store.$patch((state) => {
      let isAnyStreaming = false;
      let isMessageDone = false;

      history.forEach((message) => {
        if (message.msg_type === "system" && message.success === false) {
          onSystemError?.(message);
        }

        if (message.msg_type === "system") {
          onSystemMessage?.(message);
          return;
        }

        if (!message.id) {
          message.id = window.crypto.randomUUID();
        }

        if (message.request_id) {
          state.streamingRequestId = message.request_id;
        }

        if (message.extras?.message?.streaming) {
          state.streamingRequestId = message.id;
          if (!state.history.has(message.id)) {
            state.history.set(message.id, message);
          } else {
            state.history.get(message.id).response += message.response;
          }

          if (message.extras?.message?.more) {
            isAnyStreaming = true;
          } else {
            const finalResponse = state.history.get(message.id).response;
            state.history.set(message.id, {
              ...message,
              response: finalResponse,
            });
            isMessageDone = true;
          }
        } else {
          state.history.set(message.id, message);
        }
      });

      if (isMessageDone) {
        store.isStoppingResponse = false;
      }
      store.isStreaming = isAnyStreaming;
    });
  };

  return {
    closeWebsocket,
    failWithMsg,
    fetchAuthTokens,
    mergeMessages,
    sendMessage,
    stopResponse,
  };
}
